// AgentCommandButton：触发 POST /api/agents/{agent_id}/command。
// - command 来自 daemon 已注册白名单（force-reload / kill / inspect / 等）。
// - 提交前可选二次确认（destructive 类命令）。
// - 提交后 Sonner toast 显示 ok / error 信息。

import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "./ConfirmDialog";
import { apiPostForm, APIError } from "@/api/client";
import { agentCommandPath } from "@/api/endpoints";
import { toast } from "@/components/ui/Sonner";

interface AgentCommandResponse {
  ok: boolean;
  command: string;
  agent_id: string;
  error?: string;
}

export interface AgentCommandButtonProps {
  agentID: string;
  command: string;
  // 按钮文案；默认显示 command。
  label?: string;
  // 是否要求二次确认；默认 false（危险 agent 操作建议 true）。
  needsConfirm?: boolean;
  // 二次确认描述（仅 needsConfirm=true 时使用）。
  confirmDescription?: string;
  // actor 用于 ConfirmDialog 摘要展示；外层从 useSession 注入。
  actor: string;
  // 额外参数（cache_root 等）。
  params?: Record<string, string>;
  // disabled（如 can_write=false）。
  disabled?: boolean;
  // 按钮 variant。
  variant?: "default" | "destructive" | "outline" | "ghost";
  size?: "default" | "sm" | "lg";
  icon?: ReactNode;
  // 成功后回调（用于触发 query invalidate）。
  onSuccess?: () => void;
}

export function AgentCommandButton({
  agentID,
  command,
  label,
  needsConfirm = false,
  confirmDescription,
  actor,
  params,
  disabled,
  variant = "outline",
  size = "sm",
  icon,
  onSuccess,
}: AgentCommandButtonProps) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);

  async function fire() {
    setPending(true);
    try {
      const body: Record<string, string> = { command };
      if (params) {
        for (const [key, value] of Object.entries(params)) body[key] = value;
      }
      const resp = await apiPostForm<AgentCommandResponse>(
        agentCommandPath(agentID),
        body,
      );
      if (resp.ok) {
        toast.success(`${command} 成功`, {
          description: `agent ${agentID}`,
        });
        onSuccess?.();
      } else {
        toast.error(`${command} 失败`, {
          description: resp.error ?? "未知错误",
        });
      }
    } catch (err) {
      const msg =
        err instanceof APIError ? err.message : err instanceof Error ? err.message : String(err);
      toast.error(`${command} 异常`, { description: msg });
    } finally {
      setPending(false);
    }
  }

  function handleClick() {
    if (needsConfirm) {
      setOpen(true);
    } else {
      void fire();
    }
  }

  return (
    <>
      <Button
        type="button"
        size={size}
        variant={variant}
        disabled={disabled || pending}
        onClick={handleClick}
      >
        {pending ? (
          "…"
        ) : (
          <>
            {icon}
            {label ?? command}
          </>
        )}
      </Button>
      {needsConfirm ? (
        <ConfirmDialog
          open={open}
          onOpenChange={setOpen}
          resource="agent"
          action={command}
          target={agentID}
          actor={actor}
          description={confirmDescription}
          onConfirm={fire}
        />
      ) : null}
    </>
  );
}
